import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Link, usePathname, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';
import SidebarIcon from '@/components/Icons/SidebarIcon';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import { ARTIFACT_NAV_RAIL_WIDTH } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import { useAuthStore, useModalStore, useUIStore } from '@/store';
import { openNewChatComposer } from '@/lib/openHomeComposer';
import { hasUnifiedMacTitlebar, MAC_HEADER_HEIGHT } from '@/lib/macTitlebar';
import MainNavigationRail from './MainNavigationRail';
// Keep the DOM-only Radix implementation out of the native render path.
const RailRecentChats = Platform.OS === 'macos'
  ? require('./RailRecentChats.macos').default
  : require('./RailRecentChats').default;

function RailAction({ label, icon, href, onPress, active, shortcut, colors, children }) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [focused, setFocused] = useState(false);
  const button = (
    <Pressable
      accessibilityRole={href ? 'link' : 'button'}
      accessibilityLabel={label}
      accessibilityState={href ? { selected: !!active } : undefined}
      aria-current={href && active ? 'page' : undefined}
      onPress={onPress}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      // Expo Link merges its child's style before Pressable evaluates it.
      // Supply an object so link targets retain their size and hover styles.
      style={StyleSheet.flatten([
        styles.action,
        { backgroundColor: active ? colors.solid_color_secondary : 'transparent' },
        (hovered || pressed || focused) && { backgroundColor: colors.solid_color_secondary },
      ])}
    >
      {(typeof children === 'function' ? children({ hovered, focused }) : children) || <Icon name={icon} size={19} color={active ? colors.text_color_primary : colors.text_color_secondary} />}
    </Pressable>
  );
  return (
    <Tooltip label={label} keyboardShortcut={shortcut} side="right">
      {href ? <Link href={href} asChild withAnchor>{button}</Link> : button}
    </Tooltip>
  );
}

/** Keeps the main navigation within reach whenever the desktop menu is collapsed. */
export default function ArtifactNavigationRail({ pwaSafeAreaTop = 0 }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const pathname = usePathname();
  const router = useRouter();
  const requireAuth = useRequireAuth();
  const hasUser = useAuthStore((state) => Boolean(state.user?.id));
  const openModal = useModalStore((state) => state.actions.openModal);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);

  const handleNewChat = useCallback(() => {
    openNewChatComposer(router);
  }, [router]);

  const handleOpenSearch = useCallback(() => {
    requireAuth(() => openModal('SearchModal'), {
      source: 'sidebar_search',
      title: t('sidebar.signInToSaveWork'),
      subtitle: t('sidebar.signInToSearchChatsSubtitle'),
      icon: 'magnifying-glass',
    });
  }, [requireAuth, openModal, t]);
  const shortcutHandlers = useMemo(() => ({ FOCUS_SEARCH: handleOpenSearch }), [handleOpenSearch]);
  useKeyboardShortcuts(shortcutHandlers);

  return (
    <View
      style={[styles.rail, {
        backgroundColor: hasUnifiedMacTitlebar ? colors.solid_color_block : colors.solid_color_tertiary,
        borderRightColor: colors.solid_color_block,
        borderRightWidth: 0,
        paddingTop: Math.max(insets.top, pwaSafeAreaTop, hasUnifiedMacTitlebar ? MAC_HEADER_HEIGHT : 0),
      }]}
    >
      {hasUnifiedMacTitlebar && <View pointerEvents="none" style={{
        position: 'absolute', top: MAC_HEADER_HEIGHT, bottom: 0, right: 0,
        width: 1, backgroundColor: colors.solid_color_block,
      }} />}
      <MainNavigationRail header={
        <View style={styles.header}>
          <RailAction label="Open sidebar" onPress={toggleSidebar} shortcut="⌘ ⇧ S" colors={colors}>
            {({ hovered, focused }) => hovered || focused ? (
              <SidebarIcon color={colors.text_color_primary} />
            ) : <Logo width={24} height={24} color={colors.text_color_primary} />}
          </RailAction>
        </View>
      }>
        <RailAction label={t('nav.routes.chat.newChat')} icon="pen-to-square" onPress={handleNewChat} colors={colors} />
        <RailAction label={t('navigation.search')} icon="magnifying-glass" onPress={handleOpenSearch} shortcut="⌘ K" colors={colors} />
        <View style={[styles.divider, { backgroundColor: colors.solid_color_block }]} />
        {hasUser ? <RailRecentChats colors={colors} /> : (
          <RailAction label={t('navigation.library')} icon="messages" href="/library" active={pathname === '/library'} colors={colors} />
        )}
      </MainNavigationRail>
    </View>
  );
}

const styles = StyleSheet.create({
  rail: { width: ARTIFACT_NAV_RAIL_WIDTH, flexShrink: 0, height: '100%', minHeight: 0, borderRightWidth: 1 },
  header: { minHeight: Platform.OS === 'macos' ? 48 : 53, alignItems: 'center', justifyContent: 'center' },
  action: { width: 44, height: 44, borderRadius: 11, alignSelf: 'center', alignItems: 'center', justifyContent: 'center' },
  divider: { width: 28, height: 1, marginVertical: 6 },
});
